import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  getAccountingActivity,
  getAccountingDocuments,
  getAccountingProfile,
  getAccountingReviewSummary,
  getAccountingSourceData,
  listAccountingReviewDocumentRequests,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import {
  accountingContextFromSearchParams,
  accountingPath,
  hasAccountingContext,
} from "./accountingRouteContext";
import { money } from "./accountingDate";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function packagePrice(cents) {
  const value = Number(cents || 0);
  if (!value) return "$0/month";
  return `$${Math.round(value / 100)}/month`;
}

export default function SalonBusinessCpaClientSummary() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const context = useMemo(
    () => accountingContextFromSearchParams(searchParams),
    [searchParams]
  );
  const accountantView = isSalonBusinessCpaAccountant(user);
  const hasContext = hasAccountingContext(context);
  const [summary, setSummary] = useState(null);
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [sourceData, setSourceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async () => {
    if (!token || !hasContext) return;
    setLoading(true);
    setError("");
    try {
      const [
        summaryData,
        profileData,
        requestData,
        documentData,
        activityData,
        source,
      ] = await Promise.all([
        getAccountingReviewSummary(token).catch(() => null),
        getAccountingProfile(token, context).catch(() => null),
        listAccountingReviewDocumentRequests(token, { ...context, limit: 120 }).catch(() => null),
        getAccountingDocuments(token, context).catch(() => null),
        getAccountingActivity(token, { ...context, limit: 60 }).catch(() => null),
        getAccountingSourceData(token, context).catch(() => null),
      ]);
      const row = (summaryData?.salons || []).find(
        (item) =>
          String(item.business_id) === String(context.business_id) &&
          String(item.salon_id) === String(context.salon_id)
      );
      setSummary(row || null);
      setProfile(profileData || null);
      setRequests(Array.isArray(requestData?.requests) ? requestData.requests : []);
      setDocuments(Array.isArray(documentData?.documents) ? documentData.documents : []);
      setActivity(Array.isArray(activityData?.events) ? activityData.events : []);
      setSourceData(source || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load this client summary.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, hasContext, token]);

  const openRequests = requests.filter(
    (request) => !["done", "cancelled"].includes(request.status)
  );
  const reviewDocuments = documents.filter((document) =>
    ["received", "being_reviewed", "needs_new_copy"].includes(document.status)
  );
  const sourceSales = sourceData?.sales_totals || {};
  const clientName =
    summary?.salon_name ||
    profile?.salon_name ||
    sourceData?.salon_name ||
    (hasContext ? `Salon ID ${context.salon_id}` : "Assigned client");

  return (
    <SalonBusinessCpaShell
      title="Client summary"
      description={
        accountantView
          ? "Review this assigned Salon ID without opening Ellyanna Booking or Ellyanna Boothshare."
          : "Review the Ellyanna Accounting client context for this salon."
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Link
            to={accountingPath("/workboard")}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Workboard
          </Link>
          <button
            type="button"
            onClick={loadData}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>
      }
    >
      {!hasContext ? (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          Choose a client from the workboard first.
        </div>
      ) : null}
      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-950">{clientName}</h2>
              {hasContext ? (
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  Salon ID {context.salon_id}
                </span>
              ) : null}
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                {summary?.accounting_access_status || profile?.accounting_access_status || "not started"}
              </span>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {summary?.business_name || profile?.business_name || sourceData?.business_name || "Business"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              ["/review", "Review"],
              ["/data", "Source data"],
              ["/documents", "Documents"],
              ["/requests", "Requests"],
              ["/monthly-review", "Monthly"],
              ["/checklist", "Checklist"],
            ].map(([path, label]) => (
              <Link
                key={path}
                to={accountingPath(path, context)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-5">
        {[
          ["Open requests", openRequests.length],
          ["Documents to review", reviewDocuments.length],
          ["Sales before tax", money(sourceSales.sales_before_tax)],
          ["Tax collected", money(sourceSales.tax_collected)],
          ["Payroll", money(sourceData?.payroll_summary?.total_pay)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{loading ? "-" : value}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Business setup summary</h2>
          <dl className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Profile
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {summary?.profile_label || profile?.client_profile_label || "Not set"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Package
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {summary?.selected_package_label || profile?.current_accounting_package_label || "Not selected"}
              </dd>
              <dd className="text-xs text-slate-500">
                {packagePrice(summary?.selected_package_price_cents || profile?.current_accounting_package_price_cents)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Payroll staff
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {summary?.payroll_staff_count != null
                  ? summary.payroll_staff_count
                  : summary?.staff_count_range_label || profile?.staff_count_range_label || "Not set"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Next step
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {summary?.next_step || "Monitor account"}
              </dd>
            </div>
          </dl>
          {summary?.package_mismatch_message ? (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {summary.package_mismatch_message}
            </div>
          ) : null}
          {summary?.missing_setup_items?.length ? (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">Setup items</p>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-600">
                {summary.missing_setup_items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Recent activity</h2>
          <div className="mt-5 space-y-3">
            {activity.length ? (
              activity.slice(0, 8).map((event) => (
                <div key={event.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{event.title}</p>
                  {event.body ? (
                    <p className="mt-1 text-sm leading-6 text-slate-600">{event.body}</p>
                  ) : null}
                  <p className="mt-1 text-xs text-slate-500">
                    {formatDateTime(event.occurred_at_utc)}
                  </p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No activity recorded yet.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Open requests</h2>
          <div className="mt-5 space-y-3">
            {openRequests.length ? (
              openRequests.slice(0, 6).map((request) => (
                <div key={request.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{request.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {request.document_type_label}
                    {request.due_date ? ` / Due ${formatDate(request.due_date)}` : ""}
                  </p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                No open requests for this client.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-950">Documents by period</h2>
          <div className="mt-5 space-y-3">
            {documents.length ? (
              documents.slice(0, 8).map((document) => (
                <div key={document.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">{document.file_name}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {document.document_type_label} / {document.status_label}
                  </p>
                  <p className="text-xs text-slate-500">
                    Uploaded {formatDate(document.created_at_utc)}
                  </p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No documents uploaded yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </SalonBusinessCpaShell>
  );
}
